// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from "react";
import apiService from "../services/ApiService";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // ตรวจสอบ token ที่เก็บไว้ใน localStorage เมื่อเริ่มต้น
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      
      // ตรวจสอบว่า token ยังใช้ได้หรือไม่
      verifyToken(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await apiService({
        method: 'GET',
        url: '/auth/verify',
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });
      
      if (response.success) {
        setUser(response.data.user);
        setToken(tokenToVerify);
      } else {
        // Token ไม่ถูกต้อง ลบข้อมูลออก
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setIsLoading(true);
      
      const response = await apiService({
        method: 'POST',
        url: '/auth/login',
        data: { username, password }
      });

      if (response.success) {
        const { token: newToken, user: userData } = response.data;
        
        // บันทึกข้อมูลลง localStorage
        localStorage.setItem("token", newToken);
        localStorage.setItem("user", JSON.stringify(userData));
        
        setToken(newToken);
        setUser(userData);
        
        return response;
      } else {
        throw new Error(response.message || 'เข้าสู่ระบบไม่สำเร็จ');
        
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // เรียก API logout (ถ้ามี)
      if (token) {
        await apiService({
          method: 'POST',
          url: '/auth/logout',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // ลบข้อมูลจาก localStorage และ state
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setToken(null);
      setUser(null);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        token, 
        isLoading, 
        login, 
        logout, 
        updateUser, 
        isAuthenticated, 
        isAdmin 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);